<template>
    <section class="section">
        <div class="container">
            <div class="columns">
                <div class="column">
                    <h1 class="title">
                        {{ translate("page.tech_account.title") }}
                    </h1>
                </div>
            </div>

            <div class="columns is-mobile is-centered" v-if="!initDone">
                <LoadingSpinner2 class="has-added-margin" />
            </div>

            <template v-else>
                <div class="buttons">
                    <button id="addTechAccountButton" class="button is-primary" @click="addModal?.open()">
                        <span class="icon">
                            <i class="fas fa-user-plus"></i>
                        </span>
                        <span>{{ translate("page.tech_account.create") }}</span>
                    </button>
                </div>

                <div v-if="Object.keys(createdPasswords).length > 0" class="notification is-light is-success">
                    <span class="icon">
                        <i class="fa fa-check" />
                    </span>
                    <span>{{ translate("page.tech_account.created") }} {{ translate("page.tech_account.once_you_refresh") }}</span>
                </div>

                <TechAccountCard
                    v-for="account in accounts"
                    :key="account.id"
                    :account="account"
                    :event-id="eventId"
                    :plain-password="createdPasswords[account.id]"
                    @updated="onAccountUpdated"
                    @deleted="onAccountDeleted"
                />

                <p v-if="accounts.length > 0" class="help">
                    {{ translate("page.tech_account.password_hidden_already") }}
                </p>
            </template>
        </div>

        <AddTechAccountModal ref="addModal" :event-id="eventId" @created="onAccountCreated" />
    </section>
</template>

<script setup lang="ts">
    import { ref, onMounted, nextTick } from "vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import { ComponentExposed } from "vue-component-type-helpers";
    import TechAccountCard from "./TechAccountCard.vue";
    import AddTechAccountModal from "./AddTechAccountModal.vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import techAccount, { TechAccount } from "../Api/techAccount";
    import { useFeatureAnnouncement } from "../Common/FeatureAnnouncements/useFeatureAnnouncement";

    const props = defineProps<{ eventId: number; currentUserId: number }>();

    const accounts = ref<TechAccount[]>([]);
    // Plain passwords of accounts created during this page visit, keyed by account id. They are never loaded from the server.
    const createdPasswords = ref<Record<string, string>>({});
    const addModal = ref<ComponentExposed<typeof AddTechAccountModal> | null>(null);
    const initDone = ref(false);

    function onAccountUpdated(updated: TechAccount): void {
        accounts.value = accounts.value.map(account => (account.id === updated.id ? updated : account));
    }

    function onAccountDeleted(id: string): void {
        accounts.value = accounts.value.filter(account => account.id !== id);
        delete createdPasswords.value[id];
    }

    function onAccountCreated(account: TechAccount, plainPassword: string): void {
        createdPasswords.value[account.id] = plainPassword;
        accounts.value = [...accounts.value, account];
    }

    onMounted(() => {
        initDone.value = false;
        return techAccount
            .getTechAccountsForEvent(props.eventId)
            .then(result => {
                accounts.value = result;
            })
            .catch(handleError)
            .finally(() => {
                initDone.value = true;

                // The add button is behind v-else of the init spinner, so wait for the DOM update.
                nextTick(() => {
                    useFeatureAnnouncement().showEligible(props.currentUserId, [
                        { key: "tech_accounts_multiple_accounts", element: "#addTechAccountButton" },
                    ]);
                });
            });
    });
</script>
